import clsx from 'clsx';
import { isEmpty } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { closeCurrentMediaMobileMenu } from 'Apollo/Store/Showroom/UserView/resolvers';
import { useGetShowroomSectionsQuery, useGetShowroomSelectedTabQuery } from 'generated/graphql';

import { cloudinaryCore, getVersion } from 'lib/cloudinary';
import { useGetCurrentShowroomData, useIsShowroomMobileVersion, useIsShowroomUserView } from 'lib/showroom';

import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';

import { ShowroomContext } from 'context/showroom/ShowroomContext';

import VideoPlayer from 'components/Common/VideoPlayer';

import ShowroomMobileMediaMenu from '../MobileMediaMenu';
import ShowroomContacts from './Contacts';
import ShowroomFooter from './Footer';
import ShowroomContentHeader from './Header';
import ShowroomContentSection from './Section';
import ShowroomContentShare from './Share';
import { useStyles } from './styles';

const ShowroomContent = () => {
	const { track } = React.useContext(ShowroomContext);
	const { t, i18n } = useTranslation();

	const showroom = useGetCurrentShowroomData();

	const { data, loading } = useGetShowroomSectionsQuery({ variables: { id: showroom.id } });
	const sections = React.useMemo(() => {
		if (loading) return [];
		return data?.getShowroom?.sections ?? [];
	}, [data, loading]);

	const isUserView = useIsShowroomUserView();
	const isMobileVersion = useIsShowroomMobileVersion();
	const {
		data: {
			showroom: {
				userView: { selectedTab }
			}
		}
	} = useGetShowroomSelectedTabQuery();

	const [selectedSection, setSelectedSection] = React.useState(0);
	const [isExpanded, setIsExpanded] = React.useState(false);

	const currentSection = sections[selectedSection];

	const classes = useStyles({
		spacing: currentSection?.options.spacing,
		zoomEnabled: currentSection?.options.zoomLayer || currentSection?.options.productLayer,
		isMobile: isMobileVersion
	});

	React.useEffect(() => {
		const currentIsMobileVersion = isMobileVersion;
		return () => {
			if (currentIsMobileVersion) {
				closeCurrentMediaMobileMenu();
			}
		};
	}, [isMobileVersion]);

	React.useEffect(() => {
		if (selectedSection > sections.length - 1) setSelectedSection(0);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showroom]);

	React.useEffect(() => {
		document.title = `Launchmetrics Galleries | ${showroom.name}`;
		track([
			{
				eventType: 'visit',
				showroomId: showroom.id
			}
		]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showroom]);

	React.useEffect(() => {
		if (showroom.language && showroom.language !== i18n.language) i18n.changeLanguage(showroom.language);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showroom.language]);

	const handleExpand = React.useCallback(() => {
		setIsExpanded(!isExpanded);
	}, [isExpanded]);

	const showroomLogoPublicUrl = React.useMemo(() => showroom.library?.logo?.publicUrl ?? showroom.tenant.logo?.publicUrl, [showroom]);
	const showroomLogoPublicId = React.useMemo(() => showroom.library?.logo?.publicId ?? showroom.tenant.logo?.publicId, [showroom]);

	const sectionTabs = React.useMemo(() => {
		return (
			<div className={classes.sectionTabsContainer}>
				<div className={classes.sectionTabsBox}>
					{sections.map((section, index) => (
						<div className={classes.sectionTabBox} key={`section-${index}`}>
							<Button
								id={`section-${index}-button`}
								className={clsx(classes.tab, {
									[classes.tabSelected]: index === selectedSection
								})}
								classes={{
									label: classes.tabLabel
								}}
								onClick={() => setSelectedSection(index)}
							>
								{section.name}
							</Button>{' '}
							{sections.length > index + 1 ? ' | ' : null}
						</div>
					))}
				</div>
			</div>
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sections, selectedSection]);

	const canDisplayHeroMedia = !!(
		showroom.displayHeroMedia &&
		showroom.heroMedia &&
		!!showroom.heroMedia.publicUrl &&
		!!showroom.heroMedia.publicId &&
		!!showroom.heroMedia.mediaType
	);
	const canDisplayHeroContent = !!showroom.displayHeroContent;

	const hero = React.useMemo(() => {
		if (!canDisplayHeroMedia && !canDisplayHeroContent) return;

		const heroMediaBanner = canDisplayHeroMedia ? (
			showroom.heroMedia.mediaType === 'image' ? (
				<div
					id="showroomHeroMedia"
					className={clsx(classes.showroomHeroBackground, { [classes.showroomHeroBackgroundMobile]: isMobileVersion })}
					style={{ backgroundImage: `url(${showroom.heroMedia.publicUrl})` }}
				></div>
			) : (
				<div
					id="showroomHeroMedia"
					className={clsx(classes.showroomHeroBackgroundVideo, { [classes.showroomHeroBackgroundMobile]: isMobileVersion })}
				>
					<VideoPlayer
						id="showroomContentHeroMediaVideo"
						videoId={showroom.heroMedia.publicId}
						cloudinaryVersion={getVersion(showroom.heroMedia.publicUrl)}
						config={{
							fullScreen: false,
							preview: false,
							controls: false,
							muted: true,
							autoPlay: true,
							loop: true
						}}
					/>
				</div>
			)
		) : null;

		const heroContent = canDisplayHeroContent
			? (() => {
					const wordLimit = 260;
					const shouldCropText = showroom.heroContent.length > wordLimit && isMobileVersion;
					const showReadMore = shouldCropText && !isExpanded;
					const showReadLess = shouldCropText && isExpanded;
					return (
						<div
							id="showroomHeroContainer"
							className={clsx(classes.showroomHeroContainer, {
								[classes.showroomHeroContainerMobile]: isMobileVersion,
								[classes.showroomHeroContainerCollapse]: showroom.heroContent.length > wordLimit,
								[classes.showroomHeroContainerNoMedia]: !heroMediaBanner
							})}
							style={{
								backgroundColor: showroom.heroBackgroundColor,
								color: showroom.heroFontColor
							}}
						>
							<h1 className={classes.showroomHeroTitle}>{showroom.heroTitle}</h1>
							<p
								id="showroomHeroContent"
								className={classes.showroomHeroContent}
								dangerouslySetInnerHTML={{
									__html: showReadMore
										? showroom.heroContent.substring(0, wordLimit).replace(/\n/g, '<br />') + '...'
										: showroom.heroContent.replace(/\n/g, '<br />')
								}}
							/>
							{showReadLess ? (
								<Button id="showroomReadLessButton" onClick={handleExpand}>
									{t('showroom.hero.readmore.close')}
								</Button>
							) : null}
							{showReadMore ? (
								<Button id="showroomReadMoreButton" onClick={handleExpand}>
									{t('showroom.hero.readmore.open')}
								</Button>
							) : null}
						</div>
					);
			  })()
			: null;

		return (
			<div
				id="showroomHero"
				className={clsx(classes.showroomHero, {
					[classes.showroomHeroMobile]: isMobileVersion,
					[classes.showroomHeroMobileMedia]: isMobileVersion && canDisplayHeroMedia
				})}
			>
				{heroMediaBanner}
				{heroContent}
			</div>
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		canDisplayHeroMedia,
		canDisplayHeroContent,
		showroom.heroMedia,
		showroom.heroContent,
		showroom.heroBackgroundColor,
		showroom.heroFontColor,
		showroom.heroTitle,
		isMobileVersion,
		isExpanded,
		handleExpand,
		t
	]);

	const showroomLogo = React.useMemo(() => {
		const logoUrl = showroomLogoPublicId
			? cloudinaryCore.url(showroomLogoPublicId, {
					version: getVersion(showroomLogoPublicUrl)
			  })
			: `${process.env.PUBLIC_URL}/logo.svg`;

		return logoUrl;
	}, [showroomLogoPublicId, showroomLogoPublicUrl]);
	return (
		<>
			<div
				className={clsx(classes.showroomContentContainer, {
					[classes.showroomContentDownloadableMobile]: isMobileVersion && showroom && showroom.downloadEnabled,
					[classes.showroomContentContainerMobile]: isMobileVersion
				})}
			>
				<ShowroomContentHeader logo={showroomLogo} selectedTab={selectedTab} />
				{showroom.libraryCollection || showroom.librarySeason ? (
					<div
						className={clsx(classes.showroomTitle, {
							[classes.showroomTitleMobile]:
								isMobileVersion && (showroom.libraryCollection || showroom.librarySeason) && canDisplayHeroMedia,
							[classes.showroomTitleMediaMobile]: isMobileVersion && canDisplayHeroMedia
						})}
					>
						{showroom.libraryCollection ? (
							<Typography component="h4" id="showroomContentCollection">
								{showroom.libraryCollection}
							</Typography>
						) : null}
						{showroom.librarySeason ? (
							<Typography component="h5" id="showroomContentSeason">
								{showroom.librarySeason}
							</Typography>
						) : null}
					</div>
				) : null}
				{hero}
				{sections?.length > 1 ? sectionTabs : null}
				<div className={clsx(classes.showroomGridContainer, { [classes.showroomGridContainerMobile]: isMobileVersion })}>
					<ShowroomContentSection selectedSection={selectedSection} />
				</div>

				{isUserView ? <ShowroomContentShare /> : null}
			</div>
			{!isEmpty(showroom.prContacts) && <ShowroomContacts />}
			<ShowroomFooter />
			{isMobileVersion && <ShowroomMobileMediaMenu />}
		</>
	);
};

export default React.memo(ShowroomContent);
